import { useState } from "react";
import { FileSpreadsheet, FileText, Loader2, Table2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilterBar, PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { useFilters } from "@/context/FilterContext";
import { cleanParams, download, errMsg } from "@/lib/api";

const FORMATS = [
  { id: "universel", title: "CSV universel", desc: "Toutes les colonnes du rapprochement et du contrôle fiscal, séparateur « ; ».", icon: Table2 },
  { id: "acomba", title: "Acomba", desc: "Écritures : 4000 revenus, 5100 commissions, 2200 TPS/TVQ, 5900 écarts.", icon: FileText },
  { id: "quickbooks", title: "QuickBooks", desc: "Fichier CSV d'import de factures (Rental Income).", icon: Receipt },
];

const ExportButton = ({ id, params, label = "Télécharger" }) => {
  const [loading, setLoading] = useState(false);
  const go = async () => {
    setLoading(true);
    try { await download(`/exports/${id}`, params, `${id}.csv`); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Button variant="outline" onClick={go} disabled={loading} className="mt-4 w-full" data-testid={`export-${id}-btn`}>
      {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{label}
    </Button>
  );
};

const years = () => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2].map(String); };

export default function Exports() {
  const { filters } = useFilters();
  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [trimestre, setTrimestre] = useState(String(Math.floor(new Date().getMonth() / 3) + 1));
  const params = cleanParams(filters);

  return (
    <div data-testid="exports-page">
      <PageHeader eyebrow="Pour votre comptable" title="Exports comptables"><FilterBar /></PageHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {FORMATS.map((f) => (
          <div key={f.id} className="rounded-lg border border-slate-200 bg-white p-5 flex flex-col rise" data-testid={`export-card-${f.id}`}>
            <f.icon className="h-5 w-5 text-emerald-700" />
            <h3 className="mt-3 font-semibold">{f.title}</h3>
            <p className="mt-1 text-xs text-slate-500 flex-1">{f.desc}</p>
            <ExportButton id={f.id} params={params} />
          </div>
        ))}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-5 flex flex-col rise" data-testid="export-card-trimestriel">
          <FileSpreadsheet className="h-5 w-5 text-emerald-700" />
          <h3 className="mt-3 font-semibold">Rapport trimestriel TPS/TVQ</h3>
          <p className="mt-1 text-xs text-slate-500">Excel : résumé par plateforme et détail, pour la déclaration.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Select value={annee} onValueChange={setAnnee}>
              <SelectTrigger className="h-9 bg-white" data-testid="export-annee-select"><SelectValue /></SelectTrigger>
              <SelectContent>{years().map((y) => <SelectItem key={y} value={y} data-testid={`export-annee-${y}`}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={trimestre} onValueChange={setTrimestre}>
              <SelectTrigger className="h-9 bg-white" data-testid="export-trimestre-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["1", "2", "3", "4"].map((t) => <SelectItem key={t} value={t} data-testid={`export-trimestre-${t}`}>T{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <ExportButton id="trimestriel" params={cleanParams({ annee, trimestre, plateforme: filters.plateforme, logement: filters.logement })} label="Télécharger (.xlsx)" />
        </div>
      </div>
      <Panel title="À savoir" className="mt-6">
        <p className="text-sm text-slate-600 leading-relaxed">
          Les exports CSV, Acomba et QuickBooks suivent les filtres (mois, plateforme, logement) en haut de page. Le calcul fiscal applique la
          taxe d'hébergement de 3,5 % sur le loyer, puis la TPS (5 %) et la TVQ (9,975 %) sur loyer + taxe d'hébergement + frais de ménage.
          Faites valider les montants par votre comptable.
        </p>
      </Panel>
    </div>
  );
}
